import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { AppLayoutContext } from '../../../providers/AppLayout'
import { useAppNavigate } from '../../../utils/url'
import { APP_UPDATE_NOTICE, SnackbarContext } from '../../../providers/SnackbarContext'
import { formatDate } from '../../../utils/formatting'

import { AppPermissions } from '../../../models/AppPermissions.model'
import { ConditionalStatusGroup } from '../../../models/ConditionalStatusGroup.model'

import UserService from '../../../services/UserService/UserService'
import { ConditionalStatusGroupService } from '../../../services/ConditionalStatusGroupService/ConditionalStatusGroupService'

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import Loading from '../../../components/Loading/Loading.component'
import Breadcrumbs from '../../../components/Breadcrumbs/Breadcrumbs.component'
import Tabs from '../../../components/Tabs/Tabs.component'
import ConditionalStatusGroupForm from '../shared/ConditionalStatusGroupForm.component'
import FormDialog from '../../../components/FormDialog/FormDialog.component'
import ConditionalStatusSearch from '../../ConditionalStatuses/ConditionalStatusSearch/ConditionalStatusSearch.route'
import { useQuery, useQueryClient } from 'react-query'
import { AppStateContext } from '../../../providers/AppStateContext'
import { ConditionalStatusGroupRelations } from '../../../models/ConditionalStatusGroupRelations.model'
import TrailSearch from '../../Trails/TrailSearch/TrailSearch.route'
import { TrailStatuses } from '../../../models/TrailStatuses.model'
import { startCase } from 'lodash'
import DetailSection from '../../../components/Sections/DetailSection.component'
import DetailHeader from '../../../components/Sections/DetailHeader.component'
import { useEditSectionParam } from '../../../components/Sections/useEditSectionParam'
import { SectionDefinition } from '../../../components/Sections/Sections.types'
import {
  ConditionalStatusGroupSectionIds,
  conditionalStatusGroupSections,
} from '../shared/ConditionalStatusGroupSections'

const ConditionalStatusGroupDetail = (): JSX.Element => {
  const navigate = useAppNavigate()
  const { id } = useParams<{ id: string }>()
  const [loadingError, setLoadingError] = useState('')

  const { user } = useAuth0()
  const hasWritePermissions = UserService.hasPermissions(user, [AppPermissions.editTrailFinder])

  const [isSaving, setIsSaving] = useState(false)
  const { isEditing, editSection, openEditor, closeEditor } = useEditSectionParam()
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false)

  const conditionalStatusGroupService = useMemo(() => new ConditionalStatusGroupService(), []) // memo to prevent re-creating service on every render

  useEffect(() => {
    AppLayoutContext.setPageName(`Conditional Status Group Detail`)
  }, [id])

  const queryClient = useQueryClient()
  const { isLoading, data: conditionalStatusGroup } = useQuery(
    [
      'ConditionalStatusGroupService.get',
      {
        id: Number(id),
        relations: [ConditionalStatusGroupRelations.trails, ConditionalStatusGroupRelations.conditionalStatuses],
      },
      AppStateContext.getRegion(),
    ],
    () =>
      conditionalStatusGroupService.get({
        id: Number(id),
        relations: [ConditionalStatusGroupRelations.trails, ConditionalStatusGroupRelations.conditionalStatuses],
      }),
    {
      onError: (err) => {
        setLoadingError(typeof err === 'string' ? err : 'Failed to load conditional status group.')
        console.error(err)
      },
    },
  )

  const handleEdit = () => {
    closeEditor()
  }

  const handleDeleteClick = () => {
    // show a confirmation dialog before deleting
    setIsDeleteConfirmationOpen(true)
  }

  const handleDelete = () => {
    if (conditionalStatusGroup?.id) {
      setIsSaving(true)
      setIsDeleteConfirmationOpen(false)
      conditionalStatusGroupService
        .delete(conditionalStatusGroup.id)
        .then(() => {
          SnackbarContext.show(
            `Conditional Status Group deleted successfully. You will now be redirected.`,
            'success',
            APP_UPDATE_NOTICE,
          )

          // after a delay of 1 second, redirect to the conditionalStatusGroup list page
          setTimeout(() => {
            navigate(`/conditional-status-groups`)
            queryClient.removeQueries([
              'ConditionalStatusGroupService.get',
              {
                id: Number(id),
                relations: [],
              },
            ])
            // invalidate all queries to ensure any queries that are using the modified record are updated
            queryClient.invalidateQueries()
          }, 1000)
        })
        .catch((err) => {
          console.error(err)
          SnackbarContext.show(`Conditional Status Group failed to delete: ${err}`, 'error')
          setIsSaving(false)
        })
    }
  }

  const tabs = useMemo(() => {
    const tabs = []
    if (conditionalStatusGroup) {
      tabs.push({
        label: 'Conditional Statuses',
        content: <ConditionalStatusSearch nested conditionalStatusGroup={conditionalStatusGroup} hideHeader />,
      })
      tabs.push({
        label: 'Trails',
        content: conditionalStatusGroup.trails?.length ? (
          <TrailSearch
            hideHeader
            hideBreadcrumbs
            hideActions
            disableImport
            disablePadding
            defaultParams={{
              ids: conditionalStatusGroup.trails?.map((trail) => trail.id),
            }}
          />
        ) : (
          <Box sx={{ p: 2 }}>
            <Typography variant="body1">No trails assigned.</Typography>
          </Box>
        ),
      })
    }
    return tabs
  }, [conditionalStatusGroup])

  const sectionById = (sectionId: ConditionalStatusGroupSectionIds) =>
    conditionalStatusGroupSections.find((section) => section.id === sectionId) as SectionDefinition

  return (
    <>
      {conditionalStatusGroup && (
        <FormDialog<ConditionalStatusGroup>
          open={isEditing}
          entity={conditionalStatusGroup}
          entityLabel="Conditional Status Group"
          onClose={closeEditor}
        >
          <ConditionalStatusGroupForm
            conditionalStatusGroup={conditionalStatusGroup}
            initialSection={editSection}
            onEdit={handleEdit}
            onCancel={closeEditor}
          />
        </FormDialog>
      )}
      <Dialog
        fullWidth
        maxWidth="xs"
        open={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent dividers>Are you sure you want to delete this conditional status group?</DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setIsDeleteConfirmationOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleDelete}>Confirm</Button>
        </DialogActions>
      </Dialog>
      <Paper sx={{ p: 2 }}>
        {isLoading && !loadingError && <Loading sx={{ py: 20 }} />}
        {loadingError && (
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ py: 20 }}>
            <Typography variant="error">{loadingError}</Typography>
          </Stack>
        )}
        {!isLoading && !loadingError && conditionalStatusGroup && (
          <>
            {isSaving && <Loading sx={{ py: 20 }} />}
            {!isSaving && (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <Breadcrumbs
                    items={() => {
                      const breadcrumbs: Array<{ label: string; href?: string }> = [
                        {
                          label: 'Trail Finder',
                          href: '/conditional-status-groups',
                        },
                      ]
                      breadcrumbs.push({ label: `Question ID: ${conditionalStatusGroup.id}` })

                      return breadcrumbs
                    }}
                  />
                  {hasWritePermissions && (
                    <Stack direction="row" justifyContent="end" alignItems="center" spacing={2}>
                      <Button variant="text" onClick={handleDeleteClick}>
                        Delete
                      </Button>
                      <Button variant="contained" onClick={() => openEditor()}>
                        Edit
                      </Button>
                    </Stack>
                  )}
                </Stack>

                {/* the only General field is the name, which is the title here, so General has no card of its own */}
                <DetailHeader
                  title={conditionalStatusGroup.name}
                  subtitle={`${conditionalStatusGroup.region} · ID ${conditionalStatusGroup.id}`}
                  meta={[
                    `Updated ${formatDate(conditionalStatusGroup.updatedAt)}`,
                    `Created ${formatDate(conditionalStatusGroup.createdAt)}`,
                  ]}
                />

                <Grid container spacing={2}>
                  {/* --- Conditional Statuses (read-only, managed in the tab) ------ */}
                  <Grid item zero={12}>
                    <DetailSection section={sectionById(ConditionalStatusGroupSectionIds.conditions)}>
                      {conditionalStatusGroup.conditionalStatuses?.length ? (
                        <Box component="ol" sx={{ m: 0, pl: 3 }}>
                          {conditionalStatusGroup.conditionalStatuses
                            .filter(
                              (conditionalStatus) =>
                                (conditionalStatus.lookbackHoursMin || conditionalStatus.lookbackHoursMax) &&
                                (conditionalStatus.precipMin || conditionalStatus.precipMax),
                            )
                            .map((conditionalStatus) => (
                              <Typography key={conditionalStatus.id} component="li" variant="body1">
                                {`${startCase(TrailStatuses[conditionalStatus.status]) || conditionalStatus.status} `}
                                if precipitation is{' '}
                                {!conditionalStatus.precipMin &&
                                  `less than or equal to ${conditionalStatus.precipMax}mm`}
                                {!conditionalStatus.precipMax &&
                                  `greater than or equal to ${conditionalStatus.precipMin}mm`}
                                {!!conditionalStatus.precipMin &&
                                  !!conditionalStatus.precipMax &&
                                  `between ${conditionalStatus.precipMin}mm and ${conditionalStatus.precipMax}mm`}
                                {!!conditionalStatus.lookbackHoursMax &&
                                  ` in the last ${
                                    conditionalStatus.lookbackHoursMin
                                      ? `${conditionalStatus.lookbackHoursMin} to `
                                      : ''
                                  }${conditionalStatus.lookbackHoursMax || 1}hrs`}
                              </Typography>
                            ))}
                        </Box>
                      ) : (
                        <Typography variant="body1" color="grey.500">
                          No conditions assigned.
                        </Typography>
                      )}
                    </DetailSection>
                  </Grid>
                </Grid>

                {tabs.length > 0 && (
                  <Box sx={{ mt: 4 }}>
                    <Tabs tabs={tabs} />
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Paper>
    </>
  )
}
export default ConditionalStatusGroupDetail
